import React, { useState, useMemo } from 'react';
import { EXPENSE_CATEGORIES } from '../constants';

const TransactionList = ({ transactions, onDeleteTransaction, formatCurrency, formatDate }) => {
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filter and Search Logic
  const filteredTransactions = useMemo(() => {
    return transactions.filter(tx => {
      const matchesType = filter === 'all' || tx.type === filter;
      const matchesCategory = !categoryFilter || tx.category === categoryFilter;
      const matchesSearch = !searchTerm ||
        tx.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tx.partner_name && tx.partner_name.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesType && matchesCategory && matchesSearch;
    });
  }, [transactions, filter, categoryFilter, searchTerm]);

  // Sort Logic
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'amount':
          aValue = Number(a.amount);
          bValue = Number(b.amount);
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'date':
        default:
          aValue = new Date(a.date).getTime();
          bValue = new Date(b.date).getTime();
          break;
      }
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [filteredTransactions, sortBy, sortOrder]);

  // Quick Stats for Filtered View
  const filteredStats = useMemo(() => {
    return filteredTransactions.reduce((acc, tx) => {
      const amt = Number(tx.amount) || 0;
      if (tx.type === 'income' || tx.type === 'loan_repayment') acc.income += amt;
      else if (tx.type === 'expense' || tx.type === 'loan_taken') acc.expenses += amt;
      return acc;
    }, { income: 0, expenses: 0 });
  }, [filteredTransactions]);

  const handleDelete = async (id, description) => {
    if (window.confirm(`Are you sure you want to delete "${description}"?`)) {
      try {
        await onDeleteTransaction(id);
      } catch (error) {
        alert('Failed to delete transaction. Please try again.');
      }
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'income': return '💰';
      case 'expense': return '💸';
      case 'loan_taken': return '💳';
      case 'loan_repayment': return '💰';
      case 'transfer': return '🔁';
      default: return '📝';
    }
  };

  const getTypeColorClass = (type) => {
    switch (type) {
      case 'income': return 'text-green';
      case 'expense': return 'text-red';
      case 'loan_taken': return 'text-orange';
      case 'loan_repayment': return 'text-green';
      case 'transfer': return 'text-blue';
      default: return 'text-gray';
    }
  };

  const getBankLabel = (bankId) => {
    if (bankId === 'alfalah') return 'Alfalah';
    if (bankId === 'meezan') return 'Meezan';
    if (bankId === 'payoneer') return 'Payoneer';
    return bankId || 'Unknown';
  };

  const resetFilters = () => {
    setFilter('all');
    setCategoryFilter('');
    setSearchTerm('');
    setSortBy('date');
    setSortOrder('desc');
  };

  return (
    <div className="fade-in">
      {/* Quick Stats Header */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="card" style={{ padding: '1rem', marginBottom: 0, borderLeft: '4px solid var(--success)' }}>
          <p className="text-xs text-muted mb-1">Filtered Income</p>
          <p className="text-lg font-bold text-green">{formatCurrency(filteredStats.income)}</p>
        </div>
        <div className="card" style={{ padding: '1rem', marginBottom: 0, borderLeft: '4px solid var(--danger)' }}>
          <p className="text-xs text-muted mb-1">Filtered Expenses</p>
          <p className="text-lg font-bold text-red">{formatCurrency(filteredStats.expenses)}</p>
        </div>
        <div className="card md:block hidden" style={{ padding: '1rem', marginBottom: 0, borderLeft: '4px solid var(--warning)' }}>
          <p className="text-xs text-muted mb-1">Filtered Net</p>
          <p className={`text-lg font-bold ${filteredStats.income - filteredStats.expenses >= 0 ? 'text-green' : 'text-red'}`}>
            {formatCurrency(filteredStats.income - filteredStats.expenses)}
          </p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex justify-between items-center mb-6 flex-mobile-col gap-4">
          <h2 className="text-2xl font-bold text-yellow mb-0">📝 Transactions</h2>
          <div className="flex gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <input
                type="text"
                placeholder="Search description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ paddingLeft: '2.5rem' }}
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50">🔍</span>
            </div>
            <button onClick={resetFilters} className="btn btn-secondary" title="Reset Filters">
              🔄
            </button>
          </div>
        </div>

        {/* Filters and Sort Controls */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="form-group mb-0">
            <label className="text-xs font-semibold text-muted mb-1 block">Type</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">All Types</option>
              <option value="income">💰 Income</option>
              <option value="expense">💸 Expenses</option>
              <option value="loan_taken">💳 Loans Taken</option>
              <option value="loan_repayment">💰 Loan Repayments</option>
              <option value="transfer">🔁 Transfers</option>
            </select>
          </div>

          <div className="form-group mb-0">
            <label className="text-xs font-semibold text-muted mb-1 block">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="form-select"
              disabled={filter !== 'all' && filter !== 'expense'}
            >
              <option value="">All Categories</option>
              {EXPENSE_CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="form-group mb-0">
            <label className="text-xs font-semibold text-muted mb-1 block">Sort By</label>
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="form-select"
              >
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="type">Type</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="btn btn-secondary"
                style={{ minWidth: '44px', padding: 0 }}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {sortedTransactions.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-4xl mb-4">🏜️</p>
          <p className="text-gray">No transactions found matching your criteria.</p>
          {(filter !== 'all' || categoryFilter || searchTerm) && (
            <button onClick={resetFilters} className="btn btn-primary mt-4" style={{ width: 'auto' }}>
              Clear All Filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {sortedTransactions.map((tx) => (
            <div key={tx.id} className="card transaction-item slide-up" style={{ padding: '1rem' }}>
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-secondary" style={{ fontSize: '1.5rem', background: 'var(--secondary-bg)' }}>
                    {getTypeIcon(tx.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg mb-0 truncate">{tx.description}</h3>
                    <div className="flex flex-wrap gap-2 mt-1 items-center">
                      <span className="text-xs text-muted">{formatDate(tx.date)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize font-semibold ${getTypeColorClass(tx.type)}`} style={{ background: 'rgba(255,255,255,0.05)' }}>
                        {tx.type.replace('_', ' ')}
                      </span>
                      {tx.category && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-gray" style={{ background: 'rgba(255,255,255,0.05)' }}>
                          {tx.category}
                        </span>
                      )}
                      {tx.bankId && (
                        <span className="text-xs px-2 py-0.5 rounded-full text-blue" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
                          🏦 {getBankLabel(tx.bankId)}
                        </span>
                      )}
                      {tx.type === 'transfer' && (
                        <span className="text-xs px-2 py-0.5 rounded-full text-blue" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)' }}>
                          {getBankLabel(tx.transferFrom)} → {getBankLabel(tx.transferTo)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4 ml-4">
                  <div className="text-right">
                    <p className={`text-xl font-bold ${getTypeColorClass(tx.type)}`}>
                      {tx.type === 'expense' || tx.type === 'loan_taken' || tx.type === 'withdrawal' ? '-' : '+'}
                      {formatCurrency(tx.amount)}
                    </p>
                    {tx.bankId === 'payoneer' && tx.amountGBP && (
                      <p className="text-xs text-muted">
                        £{tx.amountGBP.toFixed(2)} @ {tx.exchangeRate}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDelete(tx.id, tx.description)}
                    className="btn btn-danger"
                    style={{ width: '40px', height: '40px', padding: 0, borderRadius: '50%' }}
                    title="Delete transaction"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <style dangerouslySetInnerHTML={{
        __html: `
        .transaction-item {
          transition: all 0.2s ease;
        }
        .transaction-item:hover {
          transform: scale(1.01);
          border-color: var(--accent-yellow);
          box-shadow: var(--glow);
        }
        @media (max-width: 768px) {
          .flex-mobile-col {
            flex-direction: column;
          }
        }
      `}} />
    </div>
  );
};

export default TransactionList;